import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  inject,
  OnInit,
  OnDestroy,
  viewChild,
} from '@angular/core';
import { DataEntity } from '@barthauer/abc/graphics/adapters/open-layers';
import {
  _AbcGalleryComponent,
  _AbcToolbarComponent,
  AbcSplitterV2Component,
  AbcPopupComponent,
  AbcTabPanelComponent,
  _AbcFormComponent,
  _AbcGridComponent,
  AbcDropDownBoxComponent,
  _AbcCheckBoxComponent,
  AbcWindowComponent,
} from '@barthauer/abc/ui-components';
import { DxToolbarModule, DxPopupModule, DxFormModule, DxTabPanelModule, DxNumberBoxModule } from 'devextreme-angular';
import { DataService } from '../services/data.service';
import {
  ClickableData,
  NodeData,
  NodeGraphicsSettings,
  NodeGraphicsSettingsViewModel,
  NodeGraphicsState,
  NodeGraphicsTable,
  StringLiteral,
} from '../models/misc.models';
import { ResourceSet } from '@barthauer/abc/core/resources';
import { ResizeEvent } from 'devextreme/ui/splitter';
import { DxSelectBoxTypes } from 'devextreme-angular/ui/select-box';
import { PromiseHelper } from '@barthauer/abc/core';
import { CoreService } from 'libs/abc/core/core.service';
import { DxiTabPanelItemComponent } from 'devextreme-angular/ui/tab-panel';
import { SelectionChangingEvent } from 'devextreme/ui/tab_panel';
import { OptionChangedEvent } from 'devextreme/ui/data_grid';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import { DataAccessComponent, DataAccessDefinition, ScalableImageComponent } from '@barthauer/basys/graphics/core';
import { DxiItemModule } from 'devextreme-angular/ui/nested';

declare const PanoramoViewer: any;

@Component({
  selector: 'lib-basys-graphics-wastewater-node-graphics',
  standalone: true,
  imports: [
    _AbcToolbarComponent,
    AbcSplitterV2Component,
    DxToolbarModule,
    DataAccessComponent,
    ScalableImageComponent,
    _AbcGalleryComponent,
    AbcPopupComponent,
    DxPopupModule,
    AbcTabPanelComponent,
    DxTabPanelModule,
    DxiTabPanelItemComponent,
    DxFormModule,
    DxiItemModule,
    _AbcGridComponent,
    AbcDropDownBoxComponent,
    _AbcCheckBoxComponent,
    DxNumberBoxModule,
  ],
  templateUrl: './node-graphics.component.html',
  styleUrl: './node-graphics.component.scss',
})
export class NodeGraphicsComponent implements OnInit, AfterViewInit, OnDestroy {
  get entity(): DataEntity {
    return this._entity;
  }
  set entity(entity: DataEntity) {
    if (this._entity !== entity) {
      const isNextNode = !!this._entity;
      this._entity = entity;

      if (isNextNode) {
        this.queryGraphics(entity.id, '', true);
      }
    }
  }
  private _entity: DataEntity;

  resourceSet = ResourceSet.Empty;
  private dataService = inject(DataService);
  private abcCoreService = inject(CoreService);
  private nodeGraphicsWindowInstance: AbcWindowComponent = null;

  private dataAccess = viewChild.required<DataAccessComponent>('dataAccess');
  dataAccessElement = viewChild.required('dataAccess', { read: ElementRef });
  private toolbar = viewChild.required<_AbcToolbarComponent>('toolbar');
  private outerSplitter = viewChild.required<AbcSplitterV2Component>('outerSplitter');
  private tabPanel = viewChild.required<AbcTabPanelComponent>('tabPanel');
  private scalableImageOutline = viewChild.required<ScalableImageComponent<ClickableData>>('scalableImageOutline');
  private scalableImageInspectionWide =
    viewChild.required<ScalableImageComponent<ClickableData>>('scalableImageInspectionWide');
  private scalableImageInspectionLong =
    viewChild.required<ScalableImageComponent<ClickableData>>('scalableImageInspectionLong');
  private gridConnectedPipes = viewChild.required<_AbcGridComponent>('gridConnectedPipes');
  private gridInspectionTexts = viewChild.required<_AbcGridComponent>('gridInspectionTexts');
  private gridRehabilitationMeasures = viewChild.required<_AbcGridComponent>('gridRehabilitationMeasures');
  private videoElement = viewChild.required<ElementRef<HTMLVideoElement>>('video');

  private panoramoDivElement = viewChild.required<ElementRef<HTMLDivElement>>('panoramoDiv');
  private panoramoViewer: any = null;
  private isPanoramoLoaded = false;

  nodeData = new NodeData();
  nodeGraphicsSettings = new NodeGraphicsSettingsViewModel(new NodeGraphicsSettings());
  private nodeGraphicsTables: NodeGraphicsTable[] = [];
  private nodeGraphicsStates: NodeGraphicsState[] = [];
  private nodeGraphicsState: NodeGraphicsState;
  private nodeId = '';
  private currentConnectedPipe?: ClickableData;
  private currentInspectionText?: ClickableData;
  private currentRehabilitationMeasure?: ClickableData;
  private currentInspectionTextPhotoUrls: string[] = [];
  photoUrls: string[] = [];
  private currentVideoUrl: string;
  private getMediaMaybe = true;

  tableRowsPipe: any[];
  tableRowsInspection: any[];
  tableRowsRehabilitation: any[];
  isPipeTable = false;
  isInspectionTable = false;
  isRehabilitationTable = false;

  get showIndicatorInGallery(): boolean {
    return this.photoUrls.length > 1;
  }
  get isLoadPanelVisible(): boolean {
    return (
      this.isQueryGraphicsPending || this.isQueryReportPending || this.isQueryPhotosPending || this.isQueryVideoPending
      // || this.isPersistSettingsPending // enable this if persistSettings() should block the user interface
    );
  }
  private isQueryGraphicsPending = true;
  private isQueryReportPending = false;
  private isQueryPhotosPending = false;
  private isQueryVideoPending = false;
  private isPersistSettingsPending = false;

  private showTable = false;
  private showMedia = false;
  isCollapsedGraphicsPane = false;
  isCollapsedInfoPane = true;
  isCollapsedTablePane = false;
  isCollapsedImageAndVideoPane = false;
  isCollapsedImagePane = false;
  isCollapsedVideoPane = false;
  isCollapsibleGraphicsPane = false;
  isCollapsibleInfoPane = false;
  isCollapsibleTablePane = true;
  isCollapsibleImageAndVideoPane = true;
  isCompact = false;
  orientationImageAndVideoPane: any = 'horizontal';
  private resizeObserver: ResizeObserver;

  isSettingsPopupVisible = false;
  private acceptSettingsPopupChanges = false;
  private lastDOMRect: DOMRect;
  private lastWasMaximizedOrUnmaximized: boolean | null = null;

  isViewOutlineDisabled = false;
  isViewInspectionWideDisabled = false;
  isViewInspectionLongDisabled = false;

  toolbarRefreshButtonOptions: any;
  toolbarResetZoomButtonOptions: any;
  toolbarPrintButtonOptions: any;
  toolbarTableButtonOptions: any;
  toolbarMediaButtonOptions: any;
  toolbarSettingsButtonOptions: any;
  toolbarConfigurationGroupSelectBoxOptions: any;
  toolbarAdditionalIdSelectBoxOptions: any;

  settingsPopupAcceptButtonOptions: any;
  settingsPopupCancelButtonOptions: any;

  loadPanelPosition: any;

  constructor(private nodeGraphicsComponentElement: ElementRef<HTMLElement>) {}

  ngOnDestroy(): void {
    this.nodeGraphicsWindowInstance?.closeDialog();
    this.isSettingsPopupVisible = false;
    this.resizeObserver?.disconnect();
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      this.isCompact = width < 800;
    });

    this.resizeObserver.observe(this.toolbar().instance.element());
  }

  async ngOnInit() {
    this.resourceSet = await this.dataAccess().getResourceSet('basys-graphics-wastewater');
    this.initializeComponent(this.resourceSet);
    this.queryGraphics(this.entity.id, '', true);
    const parentPopupWindow = this.nodeGraphicsComponentElement.nativeElement.parentElement;

    if (!parentPopupWindow) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries, observer) => {
      if (!entries.length || !entries[0].target) {
        return;
      }

      if (!this.loadPanelPosition) {
        this.loadPanelPosition = {
          at: 'center',
          my: 'center',
          of: this.dataAccessElement().nativeElement,
        };
      }

      const parentWindow = entries[0].target.parentElement;
      const imageTabsContainer = this.getImageTabsContainer();

      if (!parentWindow || !imageTabsContainer) {
        return;
      }

      const parentHeight = entries[0].contentRect.height;
      const toolbarHeight = this.toolbar().instance.element().getBoundingClientRect().height;
      const outerSplitter = this.outerSplitter();
      outerSplitter._setOption('height', parentHeight - toolbarHeight);

      const wasMaximizedOrUnmaximized = this.wasMaximizedOrUnmaximized(parentWindow);
      this.getAllClickableImages().forEach((clickableImage) =>
        clickableImage.size.set({
          width: imageTabsContainer.clientWidth,
          height: imageTabsContainer.clientHeight,
          resetImage:
            wasMaximizedOrUnmaximized ||
            this.lastWasMaximizedOrUnmaximized === false ||
            clickableImage.zoomLevel <= 1.0,
        }),
      );
      this.lastWasMaximizedOrUnmaximized = wasMaximizedOrUnmaximized;
    });
    resizeObserver.observe(parentPopupWindow);
  }

  private initializeComponent(resourceSet: ResourceSet): void {
    this.toolbarRefreshButtonOptions = {
      focusStateEnabled: false,
      icon: resourceSet.svgUrl('Refresh-Image'),
      hint: resourceSet.get('Refresh'),
      onClick: () => {
        this.queryGraphics(this.nodeId, this.getView(), true);
      },
    };
    this.toolbarResetZoomButtonOptions = {
      focusStateEnabled: false,
      icon: resourceSet.svgUrl('ResetZoom-Image'),
      hint: resourceSet.get('ResetZoom'),
      onClick: () => {
        this.getAllClickableImages().forEach((clickableImage) => clickableImage.resetImage());
      },
    };
    this.toolbarPrintButtonOptions = {
      disabled: false,
      focusStateEnabled: false,
      icon: resourceSet.svgUrl('Print-Image'),
      hint: resourceSet.get('Print'),
      onClick: () => {
        this.queryReport(this.nodeId, this.nodeGraphicsState.reportFilename);
      },
    };
    this.toolbarTableButtonOptions = {
      type: 'normal',
      focusStateEnabled: false,
      icon: resourceSet.svgUrl('Table-Image'),
      hint: resourceSet.get('Table'),
      onClick: () => {
        this.setSplittersCollapsed(!this.showTable, null);
        this.toolbarTableButtonOptions.type = this.showTable ? 'default' : 'normal';
        this.resetToolbarTableButtonOptions();
        this.persistSettings(this.nodeId, this.getView());
        setTimeout(() => this.getAllClickableImages().forEach((clickableImage) => clickableImage.resetImage()));
      },
    };
    this.toolbarMediaButtonOptions = {
      type: 'normal',
      disabled: false,
      focusStateEnabled: false,
      icon: resourceSet.svgUrl('Media-Image'),
      hint: resourceSet.get('Media'),
      onClick: () => {
        this.setSplittersCollapsed(null, !this.showMedia);
        this.toolbarMediaButtonOptions.type = this.showMedia ? 'default' : 'normal';

        if (!this.nodeGraphicsState.inspectionId) {
          this.toolbarMediaButtonOptions.disabled = true;
        }

        this.resetToolbarMediaButtonOptions();

        if (this.showMedia) {
          if (this.currentInspectionText?.hasPhoto) {
            this.queryPhotos(this.nodeId, this.currentInspectionText.id);
          }

          this.queryVideo(
            this.nodeId,
            this.currentInspectionText?.hasVideo ? this.currentInspectionText.id : undefined,
          );
        } else {
          this.currentInspectionTextPhotoUrls = [];
          this.photoUrls = [];
          this.clearVideoControl();
          this.persistSettings(this.nodeId, this.getView());
        }

        setTimeout(() => this.getAllClickableImages().forEach((clickableImage) => clickableImage.resetImage()));
      },
    };
    this.toolbarSettingsButtonOptions = {
      focusStateEnabled: false,
      icon: resourceSet.svgUrl('Settings-Image'),
      hint: resourceSet.get('Settings'),
      onClick: () => {
        const configurationGroupEntryInspectionLong = this.nodeGraphicsSettings.configurationGroupEntryInspectionLong;
        this.nodeGraphicsSettings.configurationGroupEntryInspectionLong = '';
        setTimeout(() => {
          this.nodeGraphicsSettings.configurationGroupEntryInspectionLong = configurationGroupEntryInspectionLong;
          this.acceptSettingsPopupChanges = false;
          this.isSettingsPopupVisible = true;
        });
      },
    };
    this.settingsPopupAcceptButtonOptions = {
      stylingMode: 'outlined',
      text: resourceSet.get('Accept'),
      onClick: () => {
        this.acceptSettingsPopupChanges = true;
        this.isSettingsPopupVisible = false;
      },
    };
    this.settingsPopupCancelButtonOptions = {
      stylingMode: 'outlined',
      text: resourceSet.get('Cancel'),
      onClick: () => {
        this.acceptSettingsPopupChanges = false;
        this.isSettingsPopupVisible = false;
      },
    };
  }

  onSelectionChanging(e: SelectionChangingEvent): void {
    if (this.suspendSelectionChanging) {
      if (this.resumeSelectionChanging) {
        this.resumeSelectionChanging = false;
        this.suspendSelectionChanging = false;
      }

      return;
    }

    e.cancel = true;

    const oldTabTitle = e.removedItems.length > 0 ? (e.removedItems[0].title ?? '') : '';
    const newTabTitle = e.addedItems.length > 0 ? (e.addedItems[0].title ?? '') : '';

    if (newTabTitle && newTabTitle !== oldTabTitle) {
      let view = '';

      if (newTabTitle === this.resourceSet.get('Outline')) {
        view = StringLiteral.ViewOutline;
      } else if (newTabTitle === this.resourceSet.get('InspectionWide')) {
        view = StringLiteral.ViewInspectionWide;
      } else if (newTabTitle === this.resourceSet.get('InspectionLong')) {
        view = StringLiteral.ViewInspectionLong;
      }

      if (view) {
        this.queryGraphics(this.nodeId, view, false);
      }
    }
  }
  private suspendSelectionChanging = false;
  private resumeSelectionChanging = false;

  private queryGraphics(nodeId: string, view: string, isInitial: boolean): void {
    this.suspendSelectionChanging = true;
    this.suspendToolbarSelectBoxesValueChanged = true;
    const isViewChange = view !== this.getView();
    const additionalId =
      !isViewChange && view === StringLiteral.ViewInspectionLong
        ? (this.toolbarAdditionalIdSelectBoxOptions?.value ?? '')
        : '';
    const currentSettings = view ? this.getSettings(isViewChange) : undefined;

    if (isInitial && this.nodeGraphicsState) {
      this.currentConnectedPipe = undefined;
      this.currentInspectionText = undefined;
      this.currentRehabilitationMeasure = undefined;
      this.currentInspectionTextPhotoUrls = [];
      this.photoUrls = [];
      this.clearVideoControl();
      this.resetAllFocusedRows();
      this.getAllClickableImages().forEach((clickableImage) => clickableImage.clearMarking());
      this.tableRowsPipe = [];
      this.tableRowsInspection = [];
      this.tableRowsRehabilitation = [];
      this.nodeGraphicsTables.length = 0;
      this.nodeGraphicsTables = [];
      this.getMediaMaybe = true;
    }

    if (this.nodeGraphicsStates) {
      if (isInitial) {
        this.nodeGraphicsStates.length = 0;
        this.nodeGraphicsStates = [];
      } else {
        let stateIndex = -1;
        const stateMatch = this.nodeGraphicsStates.find((state, index) => {
          stateIndex = index;

          if (state.view === view) {
            if (isViewChange) {
              return state.isActive;
            } else {
              switch (view) {
                case StringLiteral.ViewOutline:
                  return (
                    state.configurationGroup === currentSettings.configurationGroupOutline &&
                    state.dimensioningName === currentSettings.dimensioningName
                  );
                case StringLiteral.ViewInspectionWide:
                  return (
                    state.configurationGroupEntry === currentSettings.configurationGroupEntryInspectionWide &&
                    state.hideStatusTextsIfNotRelevant === currentSettings.hideStatusTextsIfNotRelevant &&
                    state.showAllPipes === currentSettings.showAllPipes
                  );
                case StringLiteral.ViewInspectionLong:
                  return (
                    state.configurationGroup === currentSettings.configurationGroupInspectionLong &&
                    state.configurationGroupEntry === currentSettings.configurationGroupEntryInspectionLong &&
                    (!state.table.isInspection ||
                      state.hideStatusTextsIfNotRelevant === currentSettings.hideStatusTextsIfNotRelevant) &&
                    (state.additionalIds.length === 0 ||
                      (additionalId && additionalId === state.additionalId) ||
                      (state.isActiveAdditionalId &&
                        (!additionalId ||
                          !state.additionalIds.some((idAndDisplayText) => idAndDisplayText.id === additionalId))))
                  );
              }
            }
          }

          return false;
        });

        if (stateMatch) {
          if (
            view === StringLiteral.ViewOutline &&
            (!this.numberEquals(stateMatch.pipeDefaultLength, currentSettings.pipeDefaultLength) ||
              !this.numberEquals(stateMatch.dimensioningSpacing, currentSettings.dimensioningSpacing))
          ) {
            this.nodeGraphicsStates.splice(stateIndex, 1);
          } else {
            if (!isViewChange) {
              for (const state of this.nodeGraphicsStates.filter((s) => s.view === view)) {
                state.isActive = false;
              }

              stateMatch.isActive = true;

              if (
                view === StringLiteral.ViewInspectionLong &&
                stateMatch.additionalIds.length > 0 &&
                !stateMatch.isActiveAdditionalId
              ) {
                this.setActiveAdditionalId(stateMatch);
              }
            }

            const clickableImage = this.getClickableImage(view);
            clickableImage.clickableFeatures.set(stateMatch.clickableData);
            clickableImage.image.set({
              imageBase64: stateMatch.imageBase64,
              width: stateMatch.imageWidth,
              height: stateMatch.imageHeight,
              initialMarking: this.refreshCurrentClickableData(stateMatch.table, stateMatch.clickableData),
            });
            this.refreshTableRows(stateMatch.table);

            if (view === StringLiteral.ViewInspectionLong) {
              this.refreshToolbarAdditionalIdSelectBox(stateMatch);
            }

            if (isViewChange) {
              this.refreshToolbarSelectBoxes(view, stateMatch.configurationGroup);
              this.resumeSelectionChanging = true;
              setTimeout(() => this.changeView(view));
            } else {
              this.suspendSelectionChanging = false;
            }

            this.nodeGraphicsState = stateMatch;
            this.setMediaButtonEnabled();
            this.setPrintButtonEnabled(view);
            this.persistSettings(nodeId, view, stateMatch.additionalId ?? '', currentSettings);
            setTimeout(() => (this.suspendToolbarSelectBoxesValueChanged = false), 100);

            return;
          }
        }
      }
    }

    this.dataAccess().callWebApi(
      new DataAccessDefinition(
        'queryGraphics',
        nodeId,
        (isPending) => {
          this.isQueryGraphicsPending = isPending;

          if (!isPending) {
            this.suspendSelectionChanging = false;
          }
        },
        undefined,
        async (data) => {
          if (nodeId !== this.entity.id) {
            return;
          }

          view = data[0].view;
          this.nodeId = nodeId;
          let nodeGraphicsTable = this.nodeGraphicsTables.find((table) => table.id === data[0].table.id);

          if (!nodeGraphicsTable) {
            nodeGraphicsTable = new NodeGraphicsTable(data[0].table);
            this.nodeGraphicsTables.push(nodeGraphicsTable);
          }

          const nodeGraphicsState = new NodeGraphicsState();
          this.nodeGraphicsState = nodeGraphicsState;
          nodeGraphicsState.isActive = true;
          nodeGraphicsState.view = view;
          nodeGraphicsState.table = nodeGraphicsTable;
          nodeGraphicsState.inspectionId = data[0].idAbwKnoteninspektion;
          nodeGraphicsState.rehabilitationId = data[0].idAbwSanierungsalternative;
          nodeGraphicsState.reportFilename = data[0].reportFilename;

          if (this.nodeGraphicsStates) {
            for (const state of this.nodeGraphicsStates.filter((s) => s.view === view)) {
              state.isActive = false;
            }

            this.nodeGraphicsStates.push(nodeGraphicsState);
          }

          if (isInitial) {
            this.nodeData = new NodeData(data[0].nodeData);
            this.nodeGraphicsSettings = new NodeGraphicsSettingsViewModel(new NodeGraphicsSettings(data[0].settings));

            this.isViewOutlineDisabled = !data[0].availableViews.some((view) => view === StringLiteral.ViewOutline);
            this.isViewInspectionWideDisabled = !data[0].availableViews.some(
              (view) => view === StringLiteral.ViewInspectionWide,
            );
            this.isViewInspectionLongDisabled = !data[0].availableViews.some(
              (view) => view === StringLiteral.ViewInspectionLong,
            );

            const tabPanel = this.tabPanel();
            tabPanel.items[0].disabled = this.isViewOutlineDisabled;
            tabPanel.items[1].disabled = this.isViewInspectionWideDisabled;
            tabPanel.items[2].disabled = this.isViewInspectionLongDisabled;

            if (!this.isViewInspectionLongDisabled) {
              this.nodeGraphicsSettings.refreshConfigurationGroupEntriesInspectionLong(
                this.nodeGraphicsSettings.nodeGraphicsSettings.configurationGroupEntriesInspectionLong[0]
                  .configurationGroup,
              );
            }

            this.setSplittersCollapsed(
              this.nodeGraphicsSettings.nodeGraphicsSettings.showTable,
              this.nodeGraphicsSettings.nodeGraphicsSettings.showMedia,
            );

            if (this.showTable && this.toolbarTableButtonOptions.type !== 'default') {
              this.toolbarTableButtonOptions.type = 'default';
              this.resetToolbarTableButtonOptions();
            }
          }

          if (isInitial || this.getMediaMaybe) {
            if (
              !isInitial &&
              !this.nodeGraphicsSettings.nodeGraphicsSettings.showMedia &&
              data[0].settings?.showMedia
            ) {
              this.nodeGraphicsSettings.nodeGraphicsSettings.showMedia = true;
              this.setSplittersCollapsed(null, true);
            }

            if (this.showMedia) {
              if (
                this.toolbarMediaButtonOptions.disabled !== false ||
                this.toolbarMediaButtonOptions.type !== 'default'
              ) {
                this.toolbarMediaButtonOptions.disabled = false;
                this.toolbarMediaButtonOptions.type = 'default';
                this.resetToolbarMediaButtonOptions();
              }

              try {
                await this.setVideo(data[0]);
              } catch (err) {
                this.dataAccess().errorService.publish(err);
              }
            }
          }

          if ((!isInitial && !this.getMediaMaybe) || !this.showMedia) {
            this.setMediaButtonEnabled();
          }

          if (this.nodeGraphicsState.inspectionId) {
            this.getMediaMaybe = false;
          }

          await PromiseHelper.delay(async () => {
            nodeGraphicsState.imageBase64 = 'data:image/png;base64,' + data[0].imageBase64;
            nodeGraphicsState.imageWidth = data[0].imageWidth;
            nodeGraphicsState.imageHeight = data[0].imageHeight;
            nodeGraphicsState.clickableData = [];

            if (data[0].features) {
              for (const feature of data[0].features) {
                var clickableFeature = new ClickableData(feature);
                nodeGraphicsState.clickableData.push(clickableFeature);
              }
            }

            const clickableImage = this.getClickableImage(view);
            clickableImage.clickableFeatures.set(nodeGraphicsState.clickableData);
            clickableImage.image.set({
              imageBase64: nodeGraphicsState.imageBase64,
              width: nodeGraphicsState.imageWidth,
              height: nodeGraphicsState.imageHeight,
              initialMarking: this.refreshCurrentClickableData(nodeGraphicsTable, nodeGraphicsState.clickableData),
            });
            this.refreshTableRows(nodeGraphicsTable);

            if (isInitial || isViewChange) {
              this.refreshToolbarSelectBoxes(view, data[0].configurationGroup);
            }

            switch (view) {
              case StringLiteral.ViewOutline:
                nodeGraphicsState.configurationGroup = data[0].configurationGroup;
                nodeGraphicsState.pipeDefaultLength = this.nodeGraphicsSettings.pipeDefaultLength;
                nodeGraphicsState.dimensioningSpacing = this.nodeGraphicsSettings.dimensioningSpacing;
                nodeGraphicsState.dimensioningName = this.nodeGraphicsSettings.dimensioningName;
                break;
              case StringLiteral.ViewInspectionWide:
                nodeGraphicsState.configurationGroupEntry =
                  this.nodeGraphicsSettings.configurationGroupEntryInspectionWide;
                nodeGraphicsState.hideStatusTextsIfNotRelevant = this.nodeGraphicsSettings.hideStatusTextsIfNotRelevant;
                nodeGraphicsState.showAllPipes = this.nodeGraphicsSettings.showAllPipes;
                break;
              case StringLiteral.ViewInspectionLong:
                nodeGraphicsState.configurationGroup = data[0].configurationGroup;
                nodeGraphicsState.configurationGroupEntry =
                  this.nodeGraphicsSettings.configurationGroupEntryInspectionLong;

                if (nodeGraphicsTable.isInspection) {
                  nodeGraphicsState.hideStatusTextsIfNotRelevant =
                    this.nodeGraphicsSettings.hideStatusTextsIfNotRelevant;
                }

                nodeGraphicsState.additionalIds = data[0].additionalIds ?? [];
                nodeGraphicsState.additionalId = data[0].additionalId ?? '';

                if (nodeGraphicsState.additionalId) {
                  this.setActiveAdditionalId(nodeGraphicsState);
                }

                this.refreshToolbarAdditionalIdSelectBox(nodeGraphicsState);
                break;
            }

            if (isInitial || isViewChange) {
              this.changeView(view);
            }

            this.setPrintButtonEnabled(view);
            setTimeout(() => (this.suspendToolbarSelectBoxesValueChanged = false), 100);
          }, 0);
        },
        [
          () =>
            this.dataService.queryNodeGraphics(
              view,
              nodeId,
              this.entity.objectType,
              additionalId,
              this.nodeGraphicsTables.map((table) => table.id),
              currentSettings,
              this.getMediaMaybe,
              isInitial,
            ),
        ],
      ),
    );
  }

  private setActiveAdditionalId(nodeGraphicsState: NodeGraphicsState): void {
    if (this.nodeGraphicsStates) {
      for (const state of this.nodeGraphicsStates.filter(
        (s) =>
          s.view === StringLiteral.ViewInspectionLong &&
          s.configurationGroup === nodeGraphicsState.configurationGroup &&
          s.configurationGroupEntry === nodeGraphicsState.configurationGroupEntry,
      )) {
        state.isActiveAdditionalId = state.additionalId === nodeGraphicsState.additionalId;
      }
    }
  }

  private refreshToolbarSelectBoxes(view: string, configurationGroup: string): void {
    switch (view) {
      case StringLiteral.ViewOutline:
        this.handleConfigurationGroupChanged = (_configurationGroup) => {
          this.queryGraphics(this.nodeId, StringLiteral.ViewOutline, false);
        };
        this.handleAdditionalIdChanged = undefined;
        this.toolbarConfigurationGroupSelectBoxOptions = this.createSelectBoxOptions(
          this.nodeGraphicsSettings.nodeGraphicsSettings.configurationGroupsOutline,
          configurationGroup,
        );
        this.toolbarAdditionalIdSelectBoxOptions = this.createSelectBoxOptions(null, null);
        break;
      case StringLiteral.ViewInspectionWide:
        this.handleConfigurationGroupChanged = undefined;
        this.handleAdditionalIdChanged = undefined;
        this.toolbarConfigurationGroupSelectBoxOptions = this.createSelectBoxOptions(null, null);
        this.toolbarAdditionalIdSelectBoxOptions = this.createSelectBoxOptions(null, null);
        break;
      case StringLiteral.ViewInspectionLong:
        this.handleConfigurationGroupChanged = (_configurationGroup) => {
          this.nodeGraphicsSettings.refreshConfigurationGroupEntriesInspectionLong(_configurationGroup);
          this.queryGraphics(this.nodeId, StringLiteral.ViewInspectionLong, false);
        };
        this.toolbarConfigurationGroupSelectBoxOptions = this.createSelectBoxOptions(
          this.nodeGraphicsSettings.nodeGraphicsSettings.configurationGroupsInspectionLong,
          configurationGroup,
        );
        break;
    }
  }

  private refreshToolbarAdditionalIdSelectBox(nodeGraphicsState: NodeGraphicsState): void {
    let label = '';

    if (nodeGraphicsState.rehabilitationId) {
      label = this.resourceSet.get('Sanierungsalternative');
    } else if (nodeGraphicsState.additionalId) {
      label = this.resourceSet.get('SecondInspection');
    }

    this.handleAdditionalIdChanged = (additionalId) => {
      this.queryGraphics(this.nodeId, StringLiteral.ViewInspectionLong, false);
    };

    this.toolbarAdditionalIdSelectBoxOptions = this.createSelectBoxOptions(
      nodeGraphicsState.additionalIds,
      nodeGraphicsState.additionalId,
      label,
    );
  }

  handleToolbarConfigurationGroupSelectBoxValueChanged(e: DxSelectBoxTypes.ValueChangedEvent): void {
    if (!this.suspendToolbarSelectBoxesValueChanged && this.handleConfigurationGroupChanged) {
      this.handleConfigurationGroupChanged(e.value);
    }
  }

  handleToolbarAdditionalIdSelectBoxValueChanged(e: DxSelectBoxTypes.ValueChangedEvent): void {
    if (!this.suspendToolbarSelectBoxesValueChanged && this.handleAdditionalIdChanged) {
      this.handleAdditionalIdChanged(e.value);
    }
  }

  private handleConfigurationGroupChanged: (configurationGroup: string) => void;
  private handleAdditionalIdChanged: (additionalId: string) => void;
  private suspendToolbarSelectBoxesValueChanged = false;

  handleSettingsPopupConfigurationEntrySelectBoxValueChanged(e: DxSelectBoxTypes.ValueChangedEvent): void {
    if (this.isSettingsPopupVisible) {
      this.nodeGraphicsSettings.configurationGroupEntryInspectionLong = e.value;
    }
  }

  private queryReport(nodeId: string, reportFilename: string): void {
    this.dataAccess().callWebApi(
      new DataAccessDefinition(
        'queryReport',
        nodeId,
        (isPending) => {
          this.isQueryReportPending = isPending;
        },
        (data) => {
          if (nodeId !== this.entity.id) {
            return;
          }

          if (data[0]?.size > 0) {
            const downloadUrl = URL.createObjectURL(data[0]);
            setTimeout(() => {
              const newWindow = this.abcCoreService.openWindow(downloadUrl);

              if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                const downloadLink = document.children[0].appendChild(document.createElement('a'));
                downloadLink.setAttribute('download', reportFilename);
                downloadLink.setAttribute('href', downloadUrl);
                downloadLink.click();
              }
            }, 100);
          }
        },
        undefined,
        [
          () =>
            this.dataService.queryNodeReport(
              this.getView(),
              nodeId,
              this.entity.objectType,
              this.toolbarAdditionalIdSelectBoxOptions.value,
              this.getSettings(false),
            ),
        ],
      ),
    );
  }

  private queryPhotos(nodeId: string, idAbwKnoteninspektionstext: string): void {
    this.dataAccess().callWebApi(
      new DataAccessDefinition(
        'queryPhotos',
        nodeId,
        (isPending) => {
          this.isQueryPhotosPending = isPending;
        },
        (data) => {
          if (nodeId !== this.entity.id) {
            return;
          }

          if (data[0].photoUrls) {
            this.currentInspectionTextPhotoUrls = [];

            for (const photoUrl of data[0].photoUrls) {
              this.currentInspectionTextPhotoUrls.push(photoUrl);
            }

            this.photoUrls = this.currentInspectionTextPhotoUrls;
            this.isCollapsedImagePane = false;
            this.isCollapsedImageAndVideoPane = false;
            this.isCollapsedInfoPane = false;
          }
        },
        undefined,
        [() => this.dataService.queryNodePhotos(idAbwKnoteninspektionstext, this.getSettings(false))],
      ),
    );
  }

  private queryVideo(nodeId: string, idAbwKnoteninspektionstext: string | undefined): void {
    const idAbwKnoteninspektion = idAbwKnoteninspektionstext ? '' : this.nodeGraphicsState.inspectionId;
    this.dataAccess().callWebApi(
      new DataAccessDefinition(
        'queryVideo',
        nodeId,
        (isPending) => {
          this.isQueryVideoPending = isPending;
        },
        undefined,
        async (data) => {
          if (nodeId !== this.entity.id) {
            return;
          }

          await this.setVideo(data[0]);
          this.isCollapsedVideoPane = false;
          this.isCollapsedImageAndVideoPane = false;
          this.isCollapsedInfoPane = false;
        },
        [
          () =>
            this.dataService.queryNodeVideo(idAbwKnoteninspektion, idAbwKnoteninspektionstext, this.getSettings(false)),
        ],
      ),
    );
  }

  private persistSettings(nodeId: string, view: string, additionalId?: string, settings?: any): void {
    this.dataAccess().callWebApi(
      new DataAccessDefinition(
        'persistSettings',
        nodeId,
        (isPending) => {
          this.isPersistSettingsPending = isPending;
        },
        (data) => {
          if (nodeId !== this.entity.id) {
            return;
          }
        },
        undefined,
        [
          () =>
            this.dataService.persistNodeSettings(
              view,
              this.entity.objectType,
              additionalId ?? this.toolbarAdditionalIdSelectBoxOptions.value,
              settings ?? this.getSettings(false),
            ),
        ],
      ),
    );
  }

  private async setVideo(data: any): Promise<void> {
    const videoUrl = data?.videoUrl ?? '';
    const isPanoramo = data?.isPanoramo ?? false;

    if (isPanoramo) {
      const videoControl = this.videoElement().nativeElement;
      const panoramoDiv = this.panoramoDivElement().nativeElement;

      videoControl.style.visibility = 'hidden';
      videoControl.style.width = '0%';
      videoControl.style.height = '0%';
      videoControl.load();
      videoControl.src = '';
      videoControl.currentTime = 0;

      if (!this.panoramoViewer) {
        if (!this.isPanoramoLoaded) {
          this.isPanoramoLoaded = true;
          await this.dataService.loadPanoramo();
        }

        this.panoramoViewer = new PanoramoViewer({
          perspectiveDiv: panoramoDiv,
        });
        this.panoramoViewer.showNavigationLines();
        this.panoramoViewer.showPositionOverlay();
      }

      if (this.currentVideoUrl !== videoUrl) {
        this.currentVideoUrl = videoUrl;

        if (videoUrl) {
          await this.panoramoViewer.selectScan(videoUrl);
          await this.panoramoViewer.openPanoView();
          this.panoramoViewer.showDividingLine();
        } else if (this.panoramoViewer) {
          this.panoramoViewer.dispose();
          this.panoramoViewer = null;
        }
      }

      if (videoUrl) {
        const panoramoDistance = data?.panoramoDistance;
        const panoramoPolarAngle = data?.panoramoPolarAngle;
        const panoramoAzimutalAngle = data?.panoramoAzimutalAngle;

        if (panoramoDistance || panoramoPolarAngle || panoramoAzimutalAngle) {
          this.panoramoViewer.setPosition(panoramoDistance, panoramoPolarAngle, panoramoAzimutalAngle);
        }
      }

      panoramoDiv.style.width = '100%';
      panoramoDiv.style.height = '100%';
      panoramoDiv.style.visibility = 'visible';
    } else {
      const videoTime = data?.videoTime ?? 0;
      this.setVideoControl(videoUrl, videoTime);
    }
  }

  private setVideoControl(videoUrl: string, videoTime: number): void {
    const videoControl = this.videoElement().nativeElement;
    const panoramoDiv = this.panoramoDivElement().nativeElement;

    panoramoDiv.style.visibility = 'hidden';
    panoramoDiv.style.width = '0%';
    panoramoDiv.style.height = '0%';

    if (this.panoramoViewer) {
      this.panoramoViewer.dispose();
      this.panoramoViewer = null;
    }

    if (this.currentVideoUrl !== videoUrl) {
      this.currentVideoUrl = videoUrl;
      videoControl.load();
      videoControl.src = videoUrl;
    }

    videoControl.currentTime = videoTime;
    videoControl.style.width = '100%';
    videoControl.style.height = '100%';
    videoControl.style.visibility = 'visible';
  }

  private clearVideoControl(): void {
    this.setVideoControl('', 0);
  }

  private getSettings(isViewChange: boolean): any {
    const settings: any = {};

    settings.graphicsDocumentIdOutline = this.nodeGraphicsSettings.graphicsDocumentIdOutline;
    settings.graphicsDocumentIdInspectionWide = this.nodeGraphicsSettings.graphicsDocumentIdInspectionWide;
    settings.graphicsDocumentIdInspectionLong = this.nodeGraphicsSettings.graphicsDocumentIdInspectionLong;

    settings.configurationGroupEntryInspectionWide = this.nodeGraphicsSettings.configurationGroupEntryInspectionWide;
    settings.configurationGroupEntryInspectionLong = this.nodeGraphicsSettings.configurationGroupEntryInspectionLong;

    settings.reportDocumentIdOutline = this.nodeGraphicsSettings.reportDocumentIdOutline;
    settings.reportDocumentIdInspection = this.nodeGraphicsSettings.reportDocumentIdInspection;

    settings.showPhotosInReport = this.nodeGraphicsSettings.showPhotosInReport;
    settings.hideStatusTextsIfNotRelevant = this.nodeGraphicsSettings.hideStatusTextsIfNotRelevant;
    settings.showAllPipes = this.nodeGraphicsSettings.showAllPipes;
    settings.pipeDefaultLength = this.nodeGraphicsSettings.pipeDefaultLength;
    settings.dimensioningSpacing = this.nodeGraphicsSettings.dimensioningSpacing;
    settings.dimensioningName = this.nodeGraphicsSettings.dimensioningName;

    if (!isViewChange) {
      settings.showTable = this.showTable;

      if (this.nodeGraphicsState?.inspectionId) {
        settings.showMedia = this.showMedia;
      }

      switch (this.getView()) {
        case StringLiteral.ViewOutline:
          settings.configurationGroupOutline = this.toolbarConfigurationGroupSelectBoxOptions.value;
          break;
        case StringLiteral.ViewInspectionWide:
          break;
        case StringLiteral.ViewInspectionLong:
          settings.configurationGroupInspectionLong = this.toolbarConfigurationGroupSelectBoxOptions.value;
          break;
      }
    }

    return settings;
  }

  private setSplittersCollapsed(showTable: boolean | null, showMedia: boolean | null): void {
    const tableShown = showTable && showTable !== this.showTable;
    const mediaShown = showMedia && showMedia !== this.showMedia;

    if (showTable !== null) {
      this.showTable = showTable;
    }

    if (showMedia !== null) {
      this.showMedia = showMedia;
    }

    if (this.showTable && this.showMedia) {
      this.setTablePaneCollapsedCollapsible(tableShown ? false : null, true);
      this.setImageAndVideoPaneCollapsedCollapsible(mediaShown ? false : null, true);
      this.setInfoPaneCollapsedCollapsible(tableShown || mediaShown ? false : null, true);
      this.setGraphicsPaneCollapsedCollapsible(null, true);
      this.orientationImageAndVideoPane = 'horizontal';
    } else if (this.showTable) {
      this.setTablePaneCollapsedCollapsible(false, false);
      this.setInfoPaneCollapsedCollapsible(tableShown ? false : null, true);
      this.setGraphicsPaneCollapsedCollapsible(null, true);

      setTimeout(() => {
        this.setImageAndVideoPaneCollapsedCollapsible(true, false);
        this.orientationImageAndVideoPane = 'horizontal';
      });
    } else if (this.showMedia) {
      this.setImageAndVideoPaneCollapsedCollapsible(false, false);
      this.setInfoPaneCollapsedCollapsible(mediaShown ? false : null, true);
      this.setGraphicsPaneCollapsedCollapsible(null, true);

      setTimeout(() => {
        this.setTablePaneCollapsedCollapsible(true, false);
        this.orientationImageAndVideoPane = 'vertical';
      });
    } else {
      this.setTablePaneCollapsedCollapsible(false, true);
      this.setImageAndVideoPaneCollapsedCollapsible(false, true);
      this.setGraphicsPaneCollapsedCollapsible(false, false);

      setTimeout(() => {
        this.setInfoPaneCollapsedCollapsible(true, false);
        this.orientationImageAndVideoPane = 'horizontal';
      });
    }
  }

  private setGraphicsPaneCollapsedCollapsible(collapsed: boolean | null, collapsible: boolean): void {
    if (collapsed !== null) {
      this.isCollapsedGraphicsPane = collapsed;
    }

    this.isCollapsibleGraphicsPane = collapsible;
  }

  private setInfoPaneCollapsedCollapsible(collapsed: boolean | null, collapsible: boolean): void {
    if (collapsed !== null) {
      this.isCollapsedInfoPane = collapsed;
    }

    this.isCollapsibleInfoPane = collapsible;
  }

  private setTablePaneCollapsedCollapsible(collapsed: boolean | null, collapsible: boolean): void {
    if (collapsed !== null) {
      this.isCollapsedTablePane = collapsed;
    }

    this.isCollapsibleTablePane = collapsible;
  }

  private setImageAndVideoPaneCollapsedCollapsible(collapsed: boolean | null, collapsible: boolean): void {
    if (collapsed !== null) {
      this.isCollapsedImageAndVideoPane = collapsed;
      this.isCollapsedImagePane = false;
      this.isCollapsedVideoPane = false;
    }

    this.isCollapsibleImageAndVideoPane = collapsible;
  }

  handleOuterSplitterCollapsedOrExpanded(e: any): void {
    if (!e.itemData) {
      return;
    }

    if (e.itemIndex === 0) {
      this.isCollapsedGraphicsPane = e.itemData.collapsed;
    } else if (e.itemIndex === 1) {
      this.isCollapsedInfoPane = e.itemData.collapsed;
    }

    this.resizeNodeImage();
  }

  handleInfoSplitterCollapsedOrExpanded(e: any): void {
    if (!e.itemData) {
      return;
    }

    if (e.itemIndex === 0) {
      this.isCollapsedTablePane = e.itemData.collapsed;
    } else if (e.itemIndex === 1) {
      this.isCollapsedImageAndVideoPane = e.itemData.collapsed;
    }
  }

  handleImageAndVideoSplitterCollapsedOrExpanded(e: any): void {
    if (!e.itemData) {
      return;
    }

    if (e.itemIndex === 0) {
      this.isCollapsedImagePane = e.itemData.collapsed;
    } else if (e.itemIndex === 1) {
      this.isCollapsedVideoPane = e.itemData.collapsed;
    }
  }

  private refreshTableRows(table: NodeGraphicsTable): void {
    if (table.isPipe) {
      this.tableRowsPipe = table.rows;
      this.isInspectionTable = false;
      this.isRehabilitationTable = false;
      this.isPipeTable = true;
    } else if (table.isInspection) {
      this.tableRowsInspection = table.rows;
      this.isPipeTable = false;
      this.isRehabilitationTable = false;
      this.isInspectionTable = true;
    } else if (table.isRehabilitation) {
      this.tableRowsRehabilitation = table.rows;
      this.isPipeTable = false;
      this.isInspectionTable = false;
      this.isRehabilitationTable = true;
    }
  }

  private createSelectBoxOptions(items: any, selectedItem: string | null, label?: string): any {
    const itemArray = [];

    if (items) {
      for (const item of items) {
        itemArray.push({ id: item.id, text: item.displayText });
      }
    }

    return {
      items: itemArray,
      value: selectedItem,
      disabled: itemArray.length === 0,
      label: label,
      displayExpr: 'text',
      valueExpr: 'id',
      width: '100%',
      gridColumns: [{ dataField: 'text', caption: '' }],
    };
  }

  handleSettingsPopupHidden(): void {
    if (!this.acceptSettingsPopupChanges || !this.nodeGraphicsSettings.hasChanges) {
      this.nodeGraphicsSettings.reset();
      return;
    }

    let isInitial =
      this.nodeGraphicsSettings.graphicsDocumentIdOutlineHasChanged ||
      this.nodeGraphicsSettings.graphicsDocumentIdInspectionWideHasChanged ||
      this.nodeGraphicsSettings.graphicsDocumentIdInspectionLongHasChanged
        ? true
        : null;
    const view = this.getView();

    if (!isInitial) {
      switch (view) {
        case StringLiteral.ViewOutline:
          if (
            this.nodeGraphicsSettings.pipeDefaultLengthHasChanged ||
            this.nodeGraphicsSettings.dimensioningSpacingHasChanged ||
            this.nodeGraphicsSettings.dimensioningNameHasChanged
          ) {
            isInitial = false;
          }
          break;
        case StringLiteral.ViewInspectionWide:
          if (
            this.nodeGraphicsSettings.configurationGroupEntryInspectionWideHasChanged ||
            this.nodeGraphicsSettings.hideStatusTextsIfNotRelevantHasChanged ||
            this.nodeGraphicsSettings.showAllPipesHasChanged
          ) {
            isInitial = false;
          }
          break;
        case StringLiteral.ViewInspectionLong:
          if (
            this.nodeGraphicsSettings.configurationGroupEntryInspectionLongHasChanged ||
            (this.nodeGraphicsState.table.isInspection &&
              this.nodeGraphicsSettings.hideStatusTextsIfNotRelevantHasChanged)
          ) {
            isInitial = false;
          }
          break;
      }
    }

    if (this.nodeGraphicsStates) {
      if (this.nodeGraphicsSettings.dimensioningNameHasChanged) {
        const oldActiveState = this.nodeGraphicsStates.find(
          (state) => state.view === StringLiteral.ViewOutline && state.isActive,
        );

        if (oldActiveState) {
          oldActiveState.isActive = false;
          const newActiveState = this.nodeGraphicsStates.find(
            (state) =>
              state.view === StringLiteral.ViewOutline &&
              state.configurationGroup === oldActiveState.configurationGroup &&
              state.dimensioningName === this.nodeGraphicsSettings.dimensioningName,
          );

          if (newActiveState) {
            newActiveState.isActive = true;
          }
        }
      }

      if (
        this.nodeGraphicsSettings.configurationGroupEntryInspectionWideHasChanged ||
        this.nodeGraphicsSettings.hideStatusTextsIfNotRelevantHasChanged ||
        this.nodeGraphicsSettings.showAllPipesHasChanged
      ) {
        const oldActiveState = this.nodeGraphicsStates.find(
          (state) => state.view === StringLiteral.ViewInspectionWide && state.isActive,
        );

        if (oldActiveState) {
          oldActiveState.isActive = false;
          const newActiveState = this.nodeGraphicsStates.find(
            (state) =>
              state.view === StringLiteral.ViewInspectionWide &&
              state.configurationGroupEntry === this.nodeGraphicsSettings.configurationGroupEntryInspectionWide &&
              state.hideStatusTextsIfNotRelevant === this.nodeGraphicsSettings.hideStatusTextsIfNotRelevant &&
              state.showAllPipes === this.nodeGraphicsSettings.showAllPipes,
          );

          if (newActiveState) {
            newActiveState.isActive = true;
          }
        }
      }

      if (
        this.nodeGraphicsSettings.configurationGroupEntryInspectionLongHasChanged ||
        this.nodeGraphicsSettings.hideStatusTextsIfNotRelevantHasChanged
      ) {
        const oldActiveState = this.nodeGraphicsStates.find(
          (state) => state.view === StringLiteral.ViewInspectionLong && state.isActive,
        );

        if (oldActiveState) {
          oldActiveState.isActive = false;
          const newActiveState = this.nodeGraphicsStates.find(
            (state) =>
              state.view === StringLiteral.ViewInspectionLong &&
              state.configurationGroup === oldActiveState.configurationGroup &&
              state.configurationGroupEntry === this.nodeGraphicsSettings.configurationGroupEntryInspectionLong &&
              (!state.table.isInspection ||
                state.hideStatusTextsIfNotRelevant === this.nodeGraphicsSettings.hideStatusTextsIfNotRelevant) &&
              (oldActiveState.additionalIds.length === 0 || oldActiveState.additionalId === state.additionalId),
          );

          if (newActiveState) {
            newActiveState.isActive = true;

            if (newActiveState.additionalId) {
              this.setActiveAdditionalId(newActiveState);
            }
          }
        }
      }
    }

    if (this.nodeGraphicsSettings.configurationGroupEntryInspectionLongHasChanged) {
      this.nodeGraphicsSettings.configurationGroupEntriesInspectionLong.forEach(
        (configurationGroupEntry) =>
          (configurationGroupEntry.isActive =
            configurationGroupEntry.id === this.nodeGraphicsSettings.configurationGroupEntryInspectionLong),
      );
    }

    if (
      this.nodeGraphicsSettings.reportDocumentIdOutlineHasChanged ||
      this.nodeGraphicsSettings.reportDocumentIdInspectionHasChanged
    ) {
      this.setPrintButtonEnabled(view);
    }

    this.nodeGraphicsSettings.accept();

    if (isInitial === null) {
      this.persistSettings(this.nodeId, view);
    } else {
      this.queryGraphics(this.nodeId, view, isInitial);
    }
  }

  private refreshCurrentClickableData(
    table: NodeGraphicsTable,
    clickableFeatures: ClickableData[],
  ): ClickableData | undefined {
    if (table.isPipe || table.isRehabilitation) {
      this.photoUrls = [];
    }

    if (table.isPipe && this.currentConnectedPipe?.id) {
      this.currentConnectedPipe = clickableFeatures.find((feature) => feature.id === this.currentConnectedPipe.id);

      if (!this.currentConnectedPipe) {
        this.resetFocusedRow(this.gridConnectedPipes());
      }

      return this.currentConnectedPipe;
    } else if (table.isInspection && this.currentInspectionText?.id) {
      this.currentInspectionText = clickableFeatures.find((feature) => feature.id === this.currentInspectionText.id);

      if (this.currentInspectionText) {
        this.photoUrls = this.currentInspectionTextPhotoUrls;
      } else {
        this.resetFocusedRow(this.gridInspectionTexts());
        this.currentInspectionTextPhotoUrls = [];
        this.photoUrls = [];
      }

      return this.currentInspectionText;
    } else if (table.isRehabilitation && this.currentRehabilitationMeasure?.id) {
      this.currentRehabilitationMeasure = clickableFeatures.find(
        (feature) => feature.id === this.currentRehabilitationMeasure.id,
      );

      if (!this.currentRehabilitationMeasure) {
        this.resetFocusedRow(this.gridRehabilitationMeasures());
      }

      return this.currentRehabilitationMeasure;
    }

    return undefined;
  }

  handleFeatureClicked(clickedData: ClickableData | undefined, isGridCall: boolean = false): void {
    const table = this.nodeGraphicsState.table;

    if (!isGridCall) {
      try {
        this.suspendFocusedRowChanged = true;
        this.getDataGrid(table).focusedRowKey = clickedData?.id;
      } finally {
        this.suspendFocusedRowChanged = false;
      }
    }

    if (table.isPipe) {
      this.currentConnectedPipe = clickedData;
    } else if (table.isInspection) {
      this.currentInspectionText = clickedData;
      this.currentInspectionTextPhotoUrls = [];
      this.photoUrls = [];

      if (clickedData && this.showMedia) {
        if (clickedData.hasPhoto) {
          this.queryPhotos(this.nodeId, clickedData.id);
        }

        if (clickedData.hasVideo) {
          this.queryVideo(this.nodeId, clickedData.id);
        }
      }
    } else if (table.isRehabilitation) {
      this.currentRehabilitationMeasure = clickedData;
    }
  }

  handleTableFocusedRowChanged(e: DxDataGridTypes.FocusedRowChangedEvent<any, any>): void {
    if (this.suspendFocusedRowChanged || !e.row?.data?.Id) {
      return;
    }

    const id = e.row.data.Id;
    const clickableFeature = this.nodeGraphicsState.clickableData.find((feature) => feature.id === id);
    const clickableImage = this.getClickableImage(this.getView());
    clickableImage.setMarking(clickableFeature);
    this.handleFeatureClicked(clickableFeature, true);
  }
  private suspendFocusedRowChanged = false;

  handleTableDataSourceChanged(e: OptionChangedEvent): void {
    if (e.name !== 'dataSource' || e.component.columnCount() > 0) {
      return;
    }

    e.component.option('columns', []);
    let isVisible = true;
    let pendingColumn: any = null;

    for (const column of this.nodeGraphicsState.table.columns) {
      if (isVisible) {
        isVisible = !!column.caption;
      }

      const gridColumn = {
        dataField: column.name,
        format: column.format,
        caption: column.caption ? column.caption : column.name,
        showInColumnChooser: !!column.caption,
        visible: isVisible,
        width: column.width,
        minWidth: column.minWidth,
      };

      if (column.isBanded) {
        if (!pendingColumn.columns) {
          pendingColumn.columns = [];
        }

        pendingColumn.columns.push(gridColumn);
        pendingColumn.alignment = 'center';
      } else {
        if (pendingColumn) {
          e.component.addColumn(pendingColumn);
        }

        pendingColumn = gridColumn;
      }
    }

    if (pendingColumn) {
      e.component.addColumn(pendingColumn);
    }
  }

  handleOuterSplitterResize(e: ResizeEvent): void {
    this.resizeNodeImage();
  }

  private resizeNodeImage(): void {
    const imageTabsContainer = this.getImageTabsContainer();

    if (imageTabsContainer) {
      this.getAllClickableImages().forEach((clickableImage) =>
        clickableImage.size.set({ width: imageTabsContainer.clientWidth, height: imageTabsContainer.clientHeight }),
      );
    }
  }

  private setMediaButtonEnabled(): void {
    const mediaButtonDisabled = !this.nodeGraphicsState.inspectionId && !this.showMedia;

    if (this.toolbarMediaButtonOptions.disabled !== mediaButtonDisabled) {
      this.toolbarMediaButtonOptions.disabled = mediaButtonDisabled;
      this.toolbarMediaButtonOptions.type = 'normal';
      this.resetToolbarMediaButtonOptions();
    }
  }

  private setPrintButtonEnabled(view: string): void {
    const printButtonDisabled =
      !this.nodeGraphicsState.reportFilename ||
      (view === StringLiteral.ViewOutline && !this.nodeGraphicsSettings.reportDocumentIdOutline) ||
      (view !== StringLiteral.ViewOutline && !this.nodeGraphicsSettings.reportDocumentIdInspection);

    if (this.toolbarPrintButtonOptions.disabled !== printButtonDisabled) {
      this.toolbarPrintButtonOptions.disabled = printButtonDisabled;
      this.resetToolbarPrintButtonOptions();
    }
  }

  private changeView(view: string): void {
    switch (view) {
      case StringLiteral.ViewOutline:
        this.tabPanel().selectedIndex = 0;
        break;
      case StringLiteral.ViewInspectionWide:
        this.tabPanel().selectedIndex = 1;
        break;
      case StringLiteral.ViewInspectionLong:
        this.tabPanel().selectedIndex = 2;
        break;
    }
  }

  private getView(): string {
    switch (this.tabPanel().selectedIndex) {
      case 0:
        return StringLiteral.ViewOutline;
      case 1:
        return StringLiteral.ViewInspectionWide;
      case 2:
        return StringLiteral.ViewInspectionLong;
    }

    throw new Error('invalid operation');
  }

  private getClickableImage(view: string): ScalableImageComponent<ClickableData> {
    switch (view) {
      case StringLiteral.ViewOutline:
        return this.scalableImageOutline();
      case StringLiteral.ViewInspectionWide:
        return this.scalableImageInspectionWide();
      case StringLiteral.ViewInspectionLong:
        return this.scalableImageInspectionLong();
    }

    throw new Error('invalid operation');
  }

  private getAllClickableImages(): ScalableImageComponent<ClickableData>[] {
    return [this.scalableImageOutline(), this.scalableImageInspectionWide(), this.scalableImageInspectionLong()];
  }

  private getDataGrid(table: NodeGraphicsTable): _AbcGridComponent {
    if (table.isPipe) {
      return this.gridConnectedPipes();
    } else if (table.isInspection) {
      return this.gridInspectionTexts();
    } else if (table.isRehabilitation) {
      return this.gridRehabilitationMeasures();
    }

    throw new Error('invalid operation');
  }

  private resetAllFocusedRows(): void {
    this.resetFocusedRow(this.gridConnectedPipes(), this.gridInspectionTexts(), this.gridRehabilitationMeasures());
  }

  private resetFocusedRow(...dataGrids: _AbcGridComponent[]): void {
    this.suspendFocusedRowChanged = true;

    try {
      for (const dataGrid of dataGrids) {
        dataGrid.focusedRowKey = undefined;
      }
    } finally {
      this.suspendFocusedRowChanged = false;
    }
  }

  private resetToolbarPrintButtonOptions(): void {
    const toolbarPrintButtonOptions = this.toolbarPrintButtonOptions;
    this.toolbarPrintButtonOptions = {};
    setTimeout(() => (this.toolbarPrintButtonOptions = toolbarPrintButtonOptions));
  }

  private resetToolbarTableButtonOptions(): void {
    const toolbarTableButtonOptions = this.toolbarTableButtonOptions;
    this.toolbarTableButtonOptions = {};
    setTimeout(() => (this.toolbarTableButtonOptions = toolbarTableButtonOptions));
  }

  private resetToolbarMediaButtonOptions(): void {
    const toolbarMediaButtonOptions = this.toolbarMediaButtonOptions;
    this.toolbarMediaButtonOptions = {};
    setTimeout(() => (this.toolbarMediaButtonOptions = toolbarMediaButtonOptions));
  }

  private wasMaximizedOrUnmaximized(element: Element): boolean | null {
    const tolerance = 10;
    const domRect = element.getBoundingClientRect();

    try {
      if (this.lastDOMRect) {
        if (
          domRect.width <= window.innerWidth &&
          window.innerWidth - domRect.width < tolerance &&
          domRect.height <= window.innerHeight &&
          window.innerHeight - domRect.height < tolerance &&
          domRect.width > this.lastDOMRect.width + tolerance &&
          domRect.height > this.lastDOMRect.height + tolerance
        ) {
          return true; // was maximized
        } else if (
          this.lastDOMRect.width <= window.innerWidth &&
          window.innerWidth - this.lastDOMRect.width < tolerance &&
          this.lastDOMRect.height <= window.innerHeight &&
          window.innerHeight - this.lastDOMRect.height < tolerance &&
          domRect.width < this.lastDOMRect.width - tolerance &&
          domRect.height < this.lastDOMRect.height - tolerance
        ) {
          return false; // was unmaximized
        }
      }

      return null;
    } finally {
      this.lastDOMRect = domRect;
    }
  }

  private getImageTabsContainer(): any {
    let imageTabsContainer: any = undefined;
    const tabPanel = this.tabPanel().instance.element();

    if (tabPanel.children.length > 1) {
      imageTabsContainer = tabPanel.children[1];

      for (let i = 0; i < 2; i++) {
        if (imageTabsContainer.children.length) {
          imageTabsContainer = imageTabsContainer.children[0];
        } else {
          imageTabsContainer = undefined;
          break;
        }
      }
    }

    return imageTabsContainer;
  }

  private numberEquals(number1: number | null, number2: number | null): boolean {
    return (number1 || number1 === 0) && (number2 || number2 === 0) && Math.abs(number2 - number1) < 0.01;
  }
  getPopupWidth(): string {
    if (window.innerWidth <= 768) {
      return '50%';
    } else if (window.innerWidth <= 1024) {
      return '80%';
    }
    return '500px';
  }
  getPopupHeight(): string {
    if (window.innerWidth <= 768) {
      return '380px';
    } else if (window.innerWidth <= 1024) {
      return '380px';
    }
    return '380px';
  }
}
